import React, { useState } from "react";
import { Plus, Trash2, Trophy } from "lucide-react";
import { updateCoachingAthlete } from "../../lib/api";
import { toast } from "sonner";

const PRIORITIES = [
  { value: "A", label: "A — Goal", cls: "bg-[var(--tir-burgundy)] text-white" },
  { value: "B", label: "B — Tune-up", cls: "bg-[#e6a35a] text-white" },
  { value: "C", label: "C — For fun", cls: "bg-[#92C58A] text-[var(--tir-ink)]" },
];

const PRIO_BY = Object.fromEntries(PRIORITIES.map((p) => [p.value, p]));

function newRace() {
  return {
    id: `tmp-${Math.random().toString(36).slice(2, 10)}`,
    name: "",
    date: "",
    distance: "",
    priority: "A",
    notes: "",
  };
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso + "T00:00:00");
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function daysFromNow(iso) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const ms = d.getTime() - Date.now();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

/**
 * Race calendar editor for an athlete.
 * Saves to the backend via PUT /coaching/athletes/{id} { races: [...] }.
 */
export default function RaceCalendar({ athlete, onChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => [...(athlete.races || [])]);
  const [saving, setSaving] = useState(false);

  const races = athlete.races || [];
  // Sort by date ascending
  const sorted = [...races].sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  const startEdit = () => {
    setDraft([...(athlete.races || [])]);
    setEditing(true);
  };
  const cancelEdit = () => setEditing(false);

  const addRow = () => setDraft([...draft, newRace()]);
  const updateRow = (i, patch) =>
    setDraft(draft.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => setDraft(draft.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      // Drop empty entries (no name AND no date)
      const cleaned = draft
        .filter((r) => (r.name || "").trim() || r.date)
        .map((r) => ({
          ...r,
          name: (r.name || "").trim(),
          distance: (r.distance || "").trim() || null,
          notes: (r.notes || "").trim() || null,
        }));
      const updated = await updateCoachingAthlete(athlete.id, { races: cleaned });
      toast.success("Race calendar updated");
      onChange?.(updated);
      setEditing(false);
    } catch {
      toast.error("Couldn't save races");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="border border-[var(--tir-border)] bg-white p-6"
      data-testid="race-calendar-card"
    >
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Trophy size={18} className="text-[var(--tir-burgundy)] mt-1 shrink-0" />
          <div>
            <div className="font-serif-display text-xl text-[var(--tir-ink)]">
              Race calendar
            </div>
            <p className="text-sm text-[var(--tir-muted)] mt-0.5">
              Goal races shape the program. Add as many as you like — A, B, and C priorities.
            </p>
          </div>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={startEdit}
            data-testid="edit-races-btn"
            className="text-xs tracking-[0.22em] uppercase font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)]"
          >
            {races.length === 0 ? "+ Add race" : "Edit"}
          </button>
        )}
      </div>

      {!editing ? (
        sorted.length === 0 ? (
          <div className="text-sm italic text-[var(--tir-muted)] border-l-2 border-[var(--tir-border)] pl-4">
            No races scheduled yet. Add a goal race to start race-back planning.
          </div>
        ) : (
          <ul className="divide-y divide-[var(--tir-border)]">
            {sorted.map((r) => {
              const days = daysFromNow(r.date);
              const prio = PRIO_BY[r.priority] || PRIORITIES[0];
              return (
                <li
                  key={r.id}
                  className="py-3 grid grid-cols-12 gap-3 items-start"
                  data-testid={`race-row-${r.id}`}
                >
                  <div className="col-span-2 sm:col-span-1 pt-0.5">
                    <span
                      className={`inline-flex items-center justify-center h-6 w-6 text-[11px] tracking-[0.05em] font-bold rounded-sm ${prio.cls}`}
                      title={prio.label}
                    >
                      {r.priority || "A"}
                    </span>
                  </div>
                  <div className="col-span-10 sm:col-span-7">
                    <div className="font-serif-display text-lg text-[var(--tir-ink)] leading-tight">
                      {r.name || "Untitled race"}
                    </div>
                    {r.notes && (
                      <div className="text-xs text-[var(--tir-muted)] mt-1 leading-snug">
                        {r.notes}
                      </div>
                    )}
                  </div>
                  <div className="col-span-7 sm:col-span-3">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
                      {formatDate(r.date)}
                    </div>
                    {days !== null && (
                      <div
                        className={`font-mono-tir text-xs mt-0.5 ${
                          days < 0
                            ? "text-[var(--tir-muted)]"
                            : days < 14
                            ? "text-[var(--tir-burgundy)] font-semibold"
                            : "text-[var(--tir-ink)]"
                        }`}
                      >
                        {days < 0
                          ? `${Math.abs(days)}d ago`
                          : days === 0
                          ? "Today"
                          : `in ${days}d`}
                      </div>
                    )}
                  </div>
                  <div className="col-span-5 sm:col-span-1 text-right text-xs font-mono-tir text-[var(--tir-muted)]">
                    {r.distance || ""}
                  </div>
                </li>
              );
            })}
          </ul>
        )
      ) : (
        <div className="space-y-3">
          {draft.length === 0 && (
            <div className="text-sm italic text-[var(--tir-muted)]">
              No races yet — click "Add race" below to get started.
            </div>
          )}
          {draft.map((r, i) => (
            <div
              key={r.id}
              className="border border-[var(--tir-border)] bg-[var(--tir-ivory)] p-3 grid grid-cols-12 gap-3 items-end"
              data-testid={`race-edit-row-${i}`}
            >
              <div className="col-span-12 sm:col-span-4">
                <label className="block text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                  Race name
                </label>
                <input
                  value={r.name}
                  onChange={(e) => updateRow(i, { name: e.target.value })}
                  placeholder="Boston Marathon"
                  data-testid={`race-name-input-${i}`}
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1.5 px-2 text-sm focus:outline-none"
                />
              </div>
              <div className="col-span-7 sm:col-span-3">
                <label className="block text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={r.date}
                  onChange={(e) => updateRow(i, { date: e.target.value })}
                  data-testid={`race-date-input-${i}`}
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1.5 px-2 text-sm focus:outline-none font-mono-tir"
                />
              </div>
              <div className="col-span-3 sm:col-span-2">
                <label className="block text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                  Distance
                </label>
                <input
                  value={r.distance || ""}
                  onChange={(e) => updateRow(i, { distance: e.target.value })}
                  placeholder="42.2k"
                  data-testid={`race-distance-input-${i}`}
                  title="Type a number — we'll assume km. Or use Half / Marathon / 10K / 100km / 13.1 mi / Ultra. Bare numbers, compact (28k), and aliases all count toward your weekly mileage."
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1.5 px-2 text-sm focus:outline-none font-mono-tir"
                />
                <div className="text-[9px] text-[var(--tir-muted)]/80 italic mt-0.5 leading-tight">
                  Just a number = km · or use <span className="font-mono-tir">Half · Marathon · 10K · 100km · 13.1 mi</span>
                </div>
              </div>
              <div className="col-span-2 sm:col-span-2">
                <label className="block text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                  Priority
                </label>
                <select
                  value={r.priority || "A"}
                  onChange={(e) => updateRow(i, { priority: e.target.value })}
                  data-testid={`race-priority-select-${i}`}
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1.5 px-2 text-sm focus:outline-none"
                >
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                </select>
              </div>
              <div className="col-span-12 sm:col-span-1 flex justify-end">
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] p-1.5"
                  aria-label="Delete race"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="col-span-12">
                <input
                  value={r.notes || ""}
                  onChange={(e) => updateRow(i, { notes: e.target.value })}
                  placeholder="Course profile, conditions, time goal…"
                  className="w-full bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 text-xs italic focus:outline-none"
                />
              </div>
            </div>
          ))}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <button
              type="button"
              onClick={addRow}
              data-testid="add-race-btn"
              className="inline-flex items-center gap-1.5 text-xs tracking-[0.22em] uppercase text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)] font-semibold"
            >
              <Plus size={13} /> Add race
            </button>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={cancelEdit}
                className="text-xs tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)] px-3 py-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="save-races-btn"
                className="inline-flex items-center gap-1.5 bg-[var(--tir-burgundy)] text-white px-4 py-2 text-xs tracking-[0.22em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save races"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
